import React, { useState, useEffect } from 'react';
import { useAnalytics } from '../../../context/AnalyticsContext';
import { useTheme } from '../../../context/ThemeContext';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import InsightsIcon from '@mui/icons-material/Insights';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CircularProgress from '@mui/material/CircularProgress';
import logger from '../../../utils/logger';
import './ModulesManagement.css';

// Category icons for modules
const CATEGORY_ICONS = {
  MOMENTUM: '\u26A1',
  TREND: '\u2197',
  VOLUME: '\u2593',
  VOLATILITY: '\u223F',
  PATTERN: '\u25B2',
  AI: '\u2728',
  SENTIMENT: '\uD83D\uDCAC',
  CORRELATION: '\uD83D\uDD17',
};

// Module presets for different analysis styles
const MODULE_PRESETS = {
  trendFollowing: {
    name: 'Trend Following',
    icon: '\u2197',
    description: 'Trade with confirmed trends using ADX and moving averages',
    modules: ['AdxTrendStrengthAnalytics', 'SmaCrossoverAnalytics', 'VolumeAnalytics'],
  },
  meanReversion: {
    name: 'Mean Reversion',
    icon: '\u21C4',
    description: 'Fade extremes at support/resistance with RSI and Bollinger',
    modules: ['RsiAnalytics', 'BollingerBandsAnalytics', 'SupportResistanceAnalytics'],
  },
  momentum: {
    name: 'Momentum',
    icon: '\u26A1',
    description: 'Catch strong moves with dual momentum confirmation',
    modules: ['RsiAnalytics', 'MacdAnalytics', 'VolumeAnalytics'],
  },
  intraday: {
    name: 'Intraday Trading',
    icon: '\u23F1',
    description: 'Day trading with VWAP and key levels',
    modules: ['VwapAnalytics', 'SupportResistanceAnalytics', 'VolumeAnalytics'],
  },
  complete: {
    name: 'Complete Analysis',
    icon: '\uD83D\uDCCA',
    description: 'Full analysis with all modules including AI',
    modules: [
      'RsiAnalytics', 'MacdAnalytics', 'SmaCrossoverAnalytics',
      'AdxTrendStrengthAnalytics', 'BollingerBandsAnalytics',
      'VwapAnalytics', 'SupportResistanceAnalytics', 'VolumeAnalytics',
      'MultiTimeframeConfluenceAnalytics'
    ],
  },
  aiOnly: {
    name: 'AI Multi-Timeframe',
    icon: '\u2728',
    description: 'AI analysis across M30, H1, H4 for confluence',
    modules: ['MultiTimeframeConfluenceAnalytics'],
  },
};

const ModulesManagement = ({ open, onClose, subscriptionStatus }) => {
  const { isDarkMode } = useTheme();
  const {
    libraryModules,
    pipelineModules,
    loadLibraryModules,
    addModule,
    activePipeline,
    loading: contextLoading,
  } = useAnalytics();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [addingModules, setAddingModules] = useState({}); // Track loading per module
  const [applyingPreset, setApplyingPreset] = useState(null);
  const [showPresets, setShowPresets] = useState(true);

  // Calculate module usage limits
  const maxModules = subscriptionStatus?.maxAnalyticsModules ?? 10;
  const currentCount = pipelineModules?.length || 0;
  const canAddMore = maxModules === -1 || currentCount < maxModules;

  // Load library modules when modal opens
  useEffect(() => {
    if (open) {
      setLoading(true);
      setError(null);
      loadLibraryModules()
        .then(() => setLoading(false))
        .catch((err) => {
          logger.error('Error loading modules:', err);
          setError('Failed to load modules');
          setLoading(false);
        });
    }
  }, [open, loadLibraryModules]);

  // Show message if no pipeline is ready
  const isReady = !contextLoading && activePipeline?.id;

  const isModuleAdded = (libraryModuleId) => {
    return pipelineModules?.some((pm) => {
      const pmId = typeof pm.libraryModuleId === 'string' ? pm.libraryModuleId : pm.libraryModuleId?.toString();
      const lmId = typeof libraryModuleId === 'string' ? libraryModuleId : libraryModuleId?.toString();
      return pmId === lmId;
    });
  };

  const handleAddModule = async (libraryModuleId) => {
    if (!activePipeline?.id) {
      setError('No active pipeline. Please select or create a pipeline first.');
      return;
    }

    if (!canAddMore) {
      setError(`Module limit reached (${maxModules}). Upgrade to add more.`);
      return;
    }

    setAddingModules(prev => ({ ...prev, [libraryModuleId]: true }));
    setError(null);

    try {
      const result = await addModule(libraryModuleId);
      if (!result) {
        setError('Failed to add module. Please try again.');
      }
    } catch (err) {
      logger.error('Error adding module:', err);
      setError('Failed to add module: ' + (err.message || 'Unknown error'));
    } finally {
      setAddingModules(prev => ({ ...prev, [libraryModuleId]: false }));
    }
  };

  const handleApplyPreset = async (presetKey) => {
    const preset = MODULE_PRESETS[presetKey];
    if (!preset) return;

    setApplyingPreset(presetKey);
    setError(null);

    try {
      // Find library modules matching the preset
      const modulesToAdd = libraryModules.filter(lm =>
        preset.modules.includes(lm.moduleClass || lm.name) && !isModuleAdded(lm.id)
      );

      // Check limits
      if (maxModules !== -1 && currentCount + modulesToAdd.length > maxModules) {
        setError(`Preset exceeds module limit (${maxModules}). Upgrade to add these modules.`);
        setApplyingPreset(null);
        return;
      }

      // Add each module sequentially
      for (const libraryModule of modulesToAdd) {
        await addModule(libraryModule.id);
      }

      setShowPresets(false);
    } catch (err) {
      logger.error('Error applying preset:', err);
      setError('Failed to apply preset');
    } finally {
      setApplyingPreset(null);
    }
  };

  if (!open) return null;

  // Filter available modules (not already added)
  const availableModules = libraryModules.filter((lm) => !isModuleAdded(lm.id) && lm.enabled !== false);

  return (
    <div className="modules-modal-overlay" onClick={onClose}>
      <div className="modules-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modules-modal-header">
          <InsightsIcon fontSize="small" />
          <span>Add Analytics Modules</span>
          <div className="modules-modal-header-actions">
            <button
              className="modules-help-btn"
              onClick={() => {/* TODO: Add help modal */}}
              title="Learn about analytics modules"
            >
              <HelpOutlineIcon fontSize="small" />
            </button>
            <button className="modules-modal-close" onClick={onClose}>
              <CloseIcon fontSize="small" />
            </button>
          </div>
        </div>

        <div className="modules-modal-content">
          {error && (
            <div className="modules-error">{error}</div>
          )}

          {/* Loading pipeline state */}
          {contextLoading && (
            <div className="modules-loading">
              <CircularProgress size={24} />
              <span>Setting up pipeline...</span>
            </div>
          )}

          {/* No pipeline ready */}
          {!contextLoading && !activePipeline?.id && (
            <div className="modules-empty-state">
              <p>No analytics pipeline available.</p>
              <p>Please select an instrument to create a pipeline.</p>
            </div>
          )}

          {/* Presets Section */}
          {isReady && !loading && showPresets && pipelineModules.length === 0 && (
            <div className="presets-section">
              <div className="presets-header">
                <span className="presets-title">Quick Start Presets</span>
                <span className="presets-subtitle">Choose an analysis style to get started</span>
              </div>
              <div className="presets-grid">
                {Object.entries(MODULE_PRESETS).map(([key, preset]) => {
                  const isApplying = applyingPreset === key;
                  return (
                    <button
                      key={key}
                      className={`preset-card preset-${key}`}
                      onClick={() => handleApplyPreset(key)}
                      disabled={applyingPreset !== null}
                    >
                      <span className="preset-icon">{preset.icon}</span>
                      <span className="preset-name">{preset.name}</span>
                      <span className="preset-description">{preset.description}</span>
                      <span className="preset-modules-count">{preset.modules.length} modules</span>
                      {isApplying && <CircularProgress size={16} className="preset-loading" />}
                    </button>
                  );
                })}
              </div>
              <div className="presets-divider">
                <span>or add modules individually</span>
              </div>
            </div>
          )}

          {/* Toggle to show presets when user already has modules */}
          {isReady && !loading && pipelineModules.length > 0 && !showPresets && (
            <button
              className="show-presets-btn"
              onClick={() => setShowPresets(true)}
            >
              Show Quick Start Presets
            </button>
          )}

          {isReady && loading ? (
            <div className="modules-loading">
              <CircularProgress size={24} />
              <span>Loading modules...</span>
            </div>
          ) : isReady && availableModules.length === 0 ? (
            <div className="modules-empty-state">
              All available modules have been added to your pipeline
            </div>
          ) : isReady && (
            <div className="modules-list compact">
              {availableModules.map((libraryModule) => {
                const isAdding = addingModules[libraryModule.id];

                return (
                  <div
                    key={libraryModule.id}
                    className={`module-item-compact ${isAdding ? 'adding' : ''}`}
                    onClick={() => !isAdding && canAddMore && handleAddModule(libraryModule.id)}
                    title={canAddMore ? 'Click to add' : 'Limit reached (Upgrade to add more)'}
                    style={{ opacity: canAddMore ? 1 : 0.6, cursor: canAddMore ? 'pointer' : 'not-allowed' }}
                  >
                    <span className="module-category-icon">
                      {CATEGORY_ICONS[libraryModule.category] || '\u2022'}
                    </span>
                    <div className="module-item-content">
                      <div className="module-item-header">
                        <span className="module-name">{libraryModule.name}</span>
                        <span className="module-category-badge">{libraryModule.category}</span>
                      </div>
                      <span className="module-description-inline">
                        {libraryModule.description || 'Market analysis module'}
                      </span>
                    </div>
                    <div className="module-add-action">
                      {isAdding ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <AddIcon fontSize="small" color={canAddMore ? 'inherit' : 'disabled'} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="modules-modal-footer">
          {/* Usage Stats */}
          <div className="modules-usage-bar">
            <div className="usage-label">
              <span>Modules</span>
              <span>{currentCount} / {maxModules === -1 ? '\u221E' : maxModules}</span>
            </div>
            <div className="usage-track">
              <div
                className="usage-fill"
                style={{
                  width: `${maxModules === -1 ? 0 : Math.min(100, (currentCount / maxModules) * 100)}%`,
                  background: canAddMore ? 'var(--color-success, #4caf50)' : 'var(--color-error, #f44336)'
                }}
              />
            </div>
          </div>

          <div className="modules-footer-actions">
            <span className="modules-count-info">
              <CheckIcon fontSize="small" />
              {pipelineModules.length} module{pipelineModules.length !== 1 ? 's' : ''} in pipeline
            </span>
            <button className="modules-done-btn" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModulesManagement;
